import { concatBasePathWithPath } from 'common/router/utils';
import {
  CharityVettingUrlType,
  type CountryCode,
  CountryCodeEnum,
} from 'features/charity-vetting/types/charityVettingTypes';
import { FormattedMessage } from 'react-intl';

import msg from '../common-flow/components/navigation-bar/messages';
import { vettingPath } from '../common-flow/entries/path';
import { frNavigationItems } from '../country-specific-flows/fr-flow/frNavigationItems';

import { commonNavigationItems } from './commonNavigationHandlers';
import { type StepName } from './formHandlers';
import { type CharityVettingNavigationItem } from './navigationTypes';

const ptNavigationItems: CharityVettingNavigationItem[] = [
  {
    ...commonNavigationItems.search,
  },
  {
    stepLabel: <FormattedMessage {...msg.charityVettingNavItemOrganisationInformation} />,
    id: CharityVettingUrlType.REGISTRATION_DETAILS as StepName,
    isCompleted: false,
    isActive: false,
    urlTypeId: ['registration', 'nipc', 'nif', 'fiscal-benefits'],
    stepPathUrl: concatBasePathWithPath(vettingPath.registrationPath, vettingPath.basePath),
  },
  {
    stepLabel: <FormattedMessage {...msg.charityVettingNavItemDonationPreferences} />,
    id: CharityVettingUrlType.DONATION_PREFERENCES as StepName,
    isCompleted: false,
    isActive: false,
    urlTypeId: [
      'donation-preferences',
      'accept-all-foods',
      'accept-all-non-food',
      'accept-all-food-and-non-food',
      'unwanted-food',
      'preparation-kitchen',
      'consumption-type',
      'haccp',
      'haccp-certificate',
    ],
    stepPathUrl: concatBasePathWithPath(vettingPath.donationPreferencesPath, vettingPath.basePath),
  },
  {
    ...commonNavigationItems.socialAccounts,
  },
  {
    ...commonNavigationItems.reviewDetails,
  },
];

const ukNavigationItems: CharityVettingNavigationItem[] = [
  {
    ...commonNavigationItems.search,
  },
  {
    id: CharityVettingUrlType.REGISTRATION_DETAILS as StepName,
    stepLabel: <FormattedMessage {...msg.charityVettingNavItemOrganisationInformation} />,
    isCompleted: false,
    isActive: false,
    urlTypeId: [
      'registration',
      'charity-number',
      'company-registration',
      'company-number',
      'public-liability-insurance',
      'public-liability-insurance-cover',
    ],
    stepPathUrl: concatBasePathWithPath(vettingPath.registrationPath, vettingPath.basePath),
  },
  {
    id: CharityVettingUrlType.DONATION_PREFERENCES as StepName,
    stepLabel: <FormattedMessage {...msg.charityVettingNavItemDonationPreferences} />,
    isCompleted: false,
    isActive: false,
    urlTypeId: [
      'donation-preferences',
      'accept-all-foods',
      'accept-all-non-food',
      'accept-all-food-and-non-food',
      'unwanted-food',
      'food-hygiene',
      'food-hygiene-certificate',
      'submit-food-hygiene-certificate',
    ],
    stepPathUrl: concatBasePathWithPath(vettingPath.donationPreferencesPath, vettingPath.basePath),
  },
  {
    ...commonNavigationItems.socialAccounts,
  },
  {
    ...commonNavigationItems.reviewDetails,
  },
];

const esNavigationItems: CharityVettingNavigationItem[] = [
  {
    ...commonNavigationItems.search,
  },
  {
    stepLabel: <FormattedMessage {...msg.charityVettingNavItemOrganisationInformation} />,
    id: CharityVettingUrlType.REGISTRATION_DETAILS as StepName,
    isCompleted: false,
    isActive: false,
    urlTypeId: ['registration', 'cif', 'donation-certificate'],
    stepPathUrl: concatBasePathWithPath(vettingPath.registrationPath, vettingPath.basePath),
  },
  {
    stepLabel: <FormattedMessage {...msg.charityVettingNavItemDonationPreferences} />,
    id: CharityVettingUrlType.DONATION_PREFERENCES as StepName,
    isCompleted: false,
    isActive: false,
    urlTypeId: [
      'donation-preferences',
      'accept-all-foods',
      'accept-all-non-food',
      'accept-all-food-and-non-food',
      'unwanted-food',
      'rgseaa-question',
      'rgseaa',
      'consumption-type',
    ],
    stepPathUrl: concatBasePathWithPath(vettingPath.donationPreferencesPath, vettingPath.basePath),
  },
  {
    ...commonNavigationItems.socialAccounts,
  },
  {
    ...commonNavigationItems.reviewDetails,
  },
];

const plNavigationItems: CharityVettingNavigationItem[] = [
  {
    ...commonNavigationItems.search,
  },
  {
    stepLabel: <FormattedMessage {...msg.charityVettingNavItemOrganisationInformation} />,
    id: CharityVettingUrlType.REGISTRATION_DETAILS as StepName,
    isCompleted: false,
    isActive: false,
    urlTypeId: ['registration', 'public-benefit', 'krs', 'nip'],
    stepPathUrl: concatBasePathWithPath(vettingPath.registrationPath, vettingPath.basePath),
  },
  {
    stepLabel: <FormattedMessage {...msg.charityVettingNavItemDonationPreferences} />,
    id: CharityVettingUrlType.DONATION_PREFERENCES as StepName,
    isCompleted: false,
    isActive: false,
    urlTypeId: [
      'donation-preferences',
      'accept-all-foods',
      'accept-all-non-food',
      'accept-all-food-and-non-food',
      'unwanted-food',
    ],
    stepPathUrl: concatBasePathWithPath(vettingPath.donationPreferencesPath, vettingPath.basePath),
  },
  {
    ...commonNavigationItems.socialAccounts,
  },
  {
    ...commonNavigationItems.reviewDetails,
  },
];

export const navigationItems = (countryIsoCode: CountryCode): CharityVettingNavigationItem[] => {
  switch (countryIsoCode) {
    case CountryCodeEnum.PT:
      return ptNavigationItems;
    case CountryCodeEnum.ES:
      return esNavigationItems;
    case CountryCodeEnum.PL:
      return plNavigationItems;
    case CountryCodeEnum.FR:
      return frNavigationItems;
    default:
      return ukNavigationItems;
  }
};
